// Chat platform v2 types (Epic #393 / Sprint 4 / issue #419).
//
// Mirrors the Prisma model shapes returned by the Sprint 2 REST surface
// (`backend/src/modules/chat/controller/chat-rest.controller.ts`). We
// deliberately do not import from the backend — keeping the wire shape
// declared client-side guarantees we get a compile error if the contract
// drifts and the consumer code reads a field that no longer exists.

export type ChatType =
  | 'DIRECT'
  | 'JOB_GROUP'
  | 'JOB_DIRECT'
  | 'JOB_BROADCAST'
  | 'SUPPORT';

export type SupportStatus =
  | 'OPEN'
  | 'ASSIGNED'
  | 'WAITING_ON_CONTRACTOR'
  | 'WAITING_ON_ADMIN'
  | 'RESOLVED'
  | 'CLOSED';

export type BroadcastTarget =
  | 'ALL_ASSIGNED'
  | 'CONFIRMED_ONLY'
  | 'NOT_CHECKED_IN'
  | 'CUSTOM_USER_IDS';

export type ParticipantRole =
  | 'CONTRACTOR'
  | 'LEAD'
  | 'ADMIN'
  | 'SUPPORT_AGENT'
  | 'OBSERVER';

export type MessageKind = 'USER_MESSAGE' | 'SYSTEM' | 'BROADCAST_NOTICE';

export interface ChatUserSnapshot {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  profileImage?: string | null;
}

export interface ChatParticipant {
  id: number;
  chatId?: number;
  userId?: number;
  unreadCount: number;
  lastReadAt: string | null;
  archivedAt: string | null;
  mutedAt: string | null;
  role: ParticipantRole;
  user?: ChatUserSnapshot;
  isArchived?: boolean;
}

export interface Chat {
  id: number;
  type: ChatType;
  groupName?: string | null;
  isGroup?: boolean;
  jobId?: number | null;
  shiftId?: number | null;
  adminId?: number | null;
  readOnly?: boolean;
  supportStatus?: SupportStatus | null;
  supportSubject?: string | null;
  supportCategory?: string | null;
  assignedAdminId?: number | null;
  lastMessage?: string | null;
  lastMessageType?: string | null;
  lastSenderId?: number | null;
  lastSender?: ChatUserSnapshot | null;
  updatedAt: string;
  createdAt: string;
  participants?: ChatParticipant[];
  /** `EVENT_REPLY:<companyId>:<eventId>:<workerId>` marks a worker-event thread (#4005). */
  dedupeKey?: string | null;
  eventId?: number | null;
  // Caller's own participant row, returned alongside the chat from
  // `GET /chats/me` and `GET /chats/:chatId`. When the caller is not a
  // participant (admin auditing), this is undefined.
  myParticipant?: ChatParticipant;
  /**
   * Legacy convenience flag from the pre-Sprint-2 `Chat.isArchived`
   * column. The new API surfaces archive state per-participant via
   * `participants[0].archivedAt`, but the field is kept here so the
   * client can read either source until the legacy column is dropped
   * in Sprint 6 (#6.4).
   */
  isArchived?: boolean;
}

export interface ChatAttachment {
  id: number;
  chatId: number;
  uploadedById: number;
  filename: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
}

export interface ChatMessage {
  id: number;
  chatId: number;
  senderId: number;
  content: string;
  type: 'text' | 'image' | 'audio' | 'file' | string;
  kind?: MessageKind;
  metadata?: Record<string, unknown> | null;
  isPinned?: boolean;
  is_deleted?: boolean;
  createdAt: string;
  sender?: ChatUserSnapshot;
  attachments?: ChatAttachment[];
  /** Sub-thread of a worker-event thread, named by the server (#4005). */
  topic?: { key: string; label: string; source?: TopicSource } | null;
  // Receipt info hydrated by the client cache from `chat:receipt:update`.
  // Each entry is `{ userId, readAt }`.
  receipts?: Array<{ userId: number; readAt: string }>;
}

export interface ChatMessagesPage {
  data: ChatMessage[];
  meta: { nextCursor: number | null; count: number };
}

export interface ChatInboxPage {
  data: Chat[];
  meta: Record<string, unknown>;
}

export interface BroadcastSendBody {
  requestKey?: string;
  title: string;
  body: string;
  target: BroadcastTarget;
  customUserIds?: number[];
  attachmentIds?: number[];
  replyBehavior?: 'READ_ONLY' | 'REPLY_PRIVATELY_TO_LEAD';
}

export interface BroadcastSendResult {
  announcementId: number;
  status: 'INBOX_POSTED';
  recipientCount: number;
}

export interface AnnouncementReceipt {
  id: number;
  jobId: number;
  title: string;
  body: string;
  status: string;
  deliveryMode: 'IMMEDIATE' | 'SCHEDULED';
  createdAt: string;
  scheduledAt?: string;
  attachments?: Array<{ originalName?: string; name?: string }>;
  result?: { inbox?: { recipientCount?: number }; push?: { sent: number; unknown: number; pending: number } };
}

export interface SupportAssignBody {
  adminId: number;
}

export interface SupportStatusBody {
  status: SupportStatus;
}

export interface SupportNoteBody {
  content: string;
}

export interface SendMessageBody {
  content: string;
  type: 'text' | 'image' | 'audio' | 'file' | string;
  kind?: MessageKind;
  clientMessageId?: string | null;
  metadata?: Record<string, unknown>;
  /** Where an event-thread message started; the server builds the label (#4005). */
  context?: TopicContext;
}

/** A record a sub-thread can start from. */
export type RecordSourceType = 'booking' | 'callTime' | 'invoice' | 'notification' | 'event';
/** A ticket has no starting record; it is numbered by its first message. */
export type TopicSourceType = RecordSourceType | 'ticket';

/** Where a sub-thread started (request, shift call time, invoice or ticket). */
export interface TopicSource {
  type: TopicSourceType;
  eventId: number;
  field: 'worker' | 'callTime' | 'invoice' | null;
  shiftId?: number;
  jobId?: number;
  invoiceId?: number;
  notificationId?: number;
  ticketNumber?: number;
}

/** Longest ticket subject the server keeps (backend `TICKET_SUBJECT_MAX`); the send pipe rejects longer ones. */
export const TICKET_SUBJECT_MAX = 80;

/** Send exactly one: continue a sub-thread, start a record's, or start a ticket. */
export interface TopicContext {
  topicKey?: string;
  source?: { type: RecordSourceType; id?: number };
  /** Only the company side may name a ticket; the server ignores a worker's subject. */
  ticket?: { subject?: string; shiftId?: number };
}

export interface TopicWorkflow {
  updatedAt?: string;
  resolvedAt: string | null;
  resolvedById: number | null;
  ownerId: number | null;
  history: Array<{ action: string; actorId: number; at: string; actorName?: string; ownerId?: number | null }>;
}
export interface ChatTopic {
  readState?: { updatedAt: string; lastReadMessageId: number | null; forceUnread: boolean } | null;
  unread?: boolean;
  displayLabel?: string;
  workflow?: TopicWorkflow | null;
  shift?: { id: number; position: string; startDate: string; startsAt: string | null; endsAt: string | null; startTime?: string; endTime?: string; timezone: string } | null;
  key: string;
  label: string;
  source: TopicSource;
  messageCount: number;
  startedAt: string;
  lastMessageAt: string;
  lastMessage: { id: number; senderId: number; content: string; type: string };
}

export interface TopicPreview {
  key: string;
  label: string;
  source: TopicSource;
  exists: boolean;
}

/** One row of the admin messages hub: a worker's thread for one event. */
export interface EventThread extends Chat {
  Event?: { id: number; title: string; startDate?: string; endDate?: string } | null;
  worker?: { id: number; firstName?: string; lastName?: string; profileImage?: string | null } | null;
  unreadCount: number;
  lastReadAt?: string | null;
}

export interface MarkReadBody {
  messageId: number;
}

export interface ArchiveBody {
  archived: boolean;
}

export interface PinMessageBody {
  isPinned: boolean;
}

export interface ChatMessageNewEvent {
  chatId: number;
  message: ChatMessage;
}

export interface ChatReceiptUpdateEvent {
  chatId: number;
  userId: number;
  messageId: number;
  readAt: string;
}

export interface JobBroadcastNewEvent {
  chatId: number;
  jobId: number;
  messageId: number;
}

export interface SupportThreadNewEvent {
  chatId: number;
  requesterId: number;
  subject: string;
}

export interface SupportStatusUpdateEvent {
  chatId: number;
  status: SupportStatus;
}

export interface ChatErrorEvent {
  event?: string;
  message: string;
}

/** The recipient of an event-office thread, independent of its active admins. */
export function eventConversationWorker(chat: Chat): ChatUserSnapshot | undefined {
  const match = /^EVENT_REPLY:[1-9]\d*:[1-9]\d*:([1-9]\d*)$/.exec(chat.dedupeKey ?? '');
  if (!match) return undefined;
  return chat.participants?.find(participant => participant.userId === Number(match[1]))?.user;
}

export interface MessageCenterRow {
  readState?: { updatedAt: string; lastReadMessageId: number | null; forceUnread: boolean } | null;
  searchMatch?: { id: number; content: string; type: string; createdAt: string } | null;
  chat: Chat & { Event?: { id: number; title: string; startDate?: string } | null };
  topic: ChatTopic;
  unread: boolean;
}
export interface MessageCenterData {
  companyId?: number;
  rows: MessageCenterRow[];
  owners: Array<{ id: number; firstName: string; lastName: string }>;
  truncated: boolean;
}
